import type { Client } from '@diana/contracts';
import { Check, Search, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../ui/index.js';
import './ClientQuickSearch.css';

const contact = (client: Client) =>
  [client.email, client.phone, client.instagram].filter(Boolean).join(' · ') ||
  'No contact details';

export function ClientQuickSearch({
  clients,
  selected,
  onSelect,
  disabled,
  loading,
  error,
  retry,
  validationError,
  onQueryChange,
  placeholder = 'Name, email, phone or Instagram',
  emptyMessage,
}: {
  clients: readonly Client[];
  selected: Client | null;
  onSelect(client: Client | null): void;
  disabled?: boolean;
  loading: boolean;
  error: boolean;
  retry(): void;
  validationError?: string;
  onQueryChange?(value: string): void;
  placeholder?: string;
  emptyMessage?: string;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const active = clients.filter((client) => !client.archivedAt);
  const search = query.trim().toLocaleLowerCase();
  const matches = active.filter((client) =>
    [client.name, client.email, client.phone, client.instagram].some((value) =>
      value?.toLocaleLowerCase().includes(search),
    ),
  );
  const visible = loading || error ? [] : matches.slice(0, 20);
  const expanded = open && !disabled;
  useEffect(() => {
    if (expanded && activeIndex >= 0)
      list.current
        ?.querySelectorAll('[role="option"]')
        [activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, expanded]);
  const choose = (client: Client) => {
    onSelect(client);
    onQueryChange?.('');
    setQuery('');
    setOpen(false);
    setActiveIndex(-1);
  };
  return (
    <div
      className="client-quick-search"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <div className="client-quick-search__control">
        <Search size={18} aria-hidden="true" />
        <input
          ref={input}
          role="combobox"
          type="text"
          aria-label="Search clients"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? `${id}-list` : undefined}
          aria-activedescendant={
            expanded && activeIndex >= 0 && visible[activeIndex]
              ? `${id}-option-${activeIndex}`
              : undefined
          }
          aria-invalid={Boolean(validationError) || undefined}
          aria-describedby={
            validationError ? `${id}-error` : selected ? `${id}-selected` : undefined
          }
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={selected ? selected.name : query}
          onFocus={(event) => {
            setOpen(true);
            setActiveIndex(-1);
            if (selected) event.currentTarget.select();
          }}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            if (selected) onSelect(null);
            onQueryChange?.(event.currentTarget.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              setActiveIndex((index) =>
                !visible.length
                  ? -1
                  : event.key === 'ArrowDown'
                    ? Math.min(index + 1, visible.length - 1)
                    : index <= 0
                      ? visible.length - 1
                      : index - 1,
              );
            } else if (event.key === 'Enter' && expanded) {
              event.preventDefault();
              if (visible[activeIndex]) choose(visible[activeIndex]!);
            } else if (event.key === 'Escape' && expanded) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              setActiveIndex(-1);
            }
          }}
        />
        {selected ? (
          <button
            type="button"
            className="client-quick-search__clear"
            aria-label="Change client"
            disabled={disabled}
            onClick={() => {
              onSelect(null);
              onQueryChange?.('');
              setQuery('');
              setOpen(true);
              setActiveIndex(-1);
              input.current?.focus();
            }}
          >
            <X size={16} />
          </button>
        ) : null}
      </div>
      {expanded ? (
        <div className="client-quick-search__dropdown">
          {loading ? (
            <p role="status">Loading clients…</p>
          ) : error ? (
            <div className="client-quick-search__error">
              <p>Clients could not be loaded. Retry or add a new client.</p>
              <Button size="small" variant="outline" onClick={retry}>
                Retry
              </Button>
            </div>
          ) : null}
          <div ref={list} role="listbox" aria-label="Clients" id={`${id}-list`}>
            {!loading && !error
              ? visible.map((client, index) => (
                  <button
                    key={client.id}
                    id={`${id}-option-${index}`}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={selected?.id === client.id}
                    data-active={index === activeIndex || undefined}
                    onMouseDown={(event) => event.preventDefault()}
                    onPointerMove={() => setActiveIndex(index)}
                    onClick={() => choose(client)}
                  >
                    <span>
                      <strong>{client.name}</strong>
                      <small>{contact(client)}</small>
                    </span>
                    {selected?.id === client.id ? <Check size={16} aria-hidden="true" /> : null}
                  </button>
                ))
              : null}
          </div>
          {!loading && !error && !visible.length ? (
            <p role="status">
              {emptyMessage ?? (active.length ? 'No matching clients' : 'No active clients yet')}
            </p>
          ) : null}
          {!loading && !error && matches.length > visible.length ? (
            <p>Type to narrow {matches.length} clients</p>
          ) : null}
        </div>
      ) : null}
      {selected ? (
        <div className="client-quick-search__selected" id={`${id}-selected`}>
          <span className="sr-only">Selected client</span>
          {contact(selected)}
        </div>
      ) : null}
      {validationError ? (
        <p className="new-request__field-error" id={`${id}-error`}>
          {validationError}
        </p>
      ) : null}
      {!expanded && loading ? <p role="status">Loading clients…</p> : null}
      {!expanded && error ? (
        <div className="client-quick-search__error">
          <p>Clients could not be loaded. Retry or add a new client.</p>
          <Button variant="outline" onClick={retry} disabled={disabled}>
            Retry
          </Button>
        </div>
      ) : null}
      {!expanded && !loading && !error && !active.length ? (
        <p className="new-request__hint">No active clients yet</p>
      ) : null}
    </div>
  );
}
